/**
 * The `convertToQueryString` function converts a filters object into a URL-encoded query string.
 * @param {Object} filters - The object containing keys and values to be converted into query parameters.
 * @returns {string} - A query string generated from the filters object.
 */
const convertToQueryString = (filters) => {
  /**
   * The `buildParam` function constructs a query parameter for a given key and value.
   * @param {string} key - The key of the parameter.
   * @param {*} value - The value associated with the key.
   * @returns {string|undefined} - A generated query parameter or undefined if the value is null or undefined.
   */
  const buildParam = (key, value) => {
    // If the value is null or undefined, do not generate any parameter.
    if (value === null || value === undefined || Number.isNaN(value)) {
      return
    }
    // If the value is a boolean, construct the parameter in the format `key=value`.
    if (typeof value === 'boolean') {
      return `${encodeURIComponent(key)}=${value}`
    }
    // If the value is an array, construct the parameters in the format `key[index]=value`.
    else if (Array.isArray(value)) {
      return value
        .map(
          (val, index) =>
            typeof val === 'object' && val !== null
              ? buildParam(`${key}[${index}]`, val) // If the array element is an object, recursively call buildParam.
              : `${encodeURIComponent(key)}[${index}]=${encodeURIComponent(val)}` // If the array element is not an object.
        )
        .join('&') // Join all parameters with '&'.
    }
    // If the value is an object, construct the parameters in the format `key[subKey]=subValue`.
    else if (typeof value === 'object') {
      return Object.entries(value)
        .map(([subKey, subValue]) => buildParam(`${key}[${subKey}]`, subValue)) // Recursively call buildParam for each key-value pair in the sub-object.
        .join('&') // Join all parameters with '&'.
    }
    // For all other types (string, number, etc.), construct the parameter in the format `key=value`.
    else {
      return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
    }
  }

  // Iterate through each key-value pair in the filters object and construct the query parameters using buildParam.
  return Object.entries(filters)
    .flatMap(([key, value]) => buildParam(key, value))
    .join('&') // Join all parameters with '&' to get the complete query string.
}

export default convertToQueryString
